import { isMobile, addSharedStylesheet } from "./utils.js";

class LocationSearchElem extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: "open" });

        const style = document.createElement("style");
        style.textContent = `
      :host {
        display: inline-flex;
        align-items: center;
      }
      .container {
        position: relative;
        display: inline-block;
      }
      .container input.location,
      .container .measurer {
        font: inherit;
        font-size: 1.8rem;
        text-align: center;
        border: 1px solid rgba(0, 0, 0, 0.25);
        border-radius: 2rem;
        height: 3rem;
        line-height: calc(3rem - 2px);
        padding: 1.5rem 3rem;
        box-sizing: border-box;
      }
      .container input.location {
        color: #000;
        outline: none;
      }
      .container input.location::placeholder {
        opacity: 1;
      }
      .container .measurer {
        position: absolute;
        top: 0;
        left: 0;
        visibility: hidden;
        pointer-events: none;
        white-space: pre;
        border-color: transparent;
      }
      .container .results {
        display: none;
        position: absolute;
        left: -50%;
        z-index: 9999;
        width: 200%;
        background-color: #FFF;
        border-radius: 5px;
        box-shadow: 2px 2px 25px rgba(0, 0, 0, 0.125);
        text-align: left;
        font-size: 1.5rem;
        line-height: 2.5rem;
        padding: 1rem;
      }
      .container .results.visible {
        display: block;
      }

      .container .results a {
        text-decoration: none;
      }

      .container .results .loading {
        margin: 2rem 0;
        text-align: center;
        color: rgba(0, 0, 0, 0.25);
      }

      .container .current_location {
        display: none;
      }
    `;

        const placeholder = this.getAttribute("placeholder") || (isMobile() ? "Location..." : "Forecast for location...");

        const container = document.createElement("span");
        container.innerHTML = `
      <div class="container">
        <div class="current_location">
          <a href="javascript:;">Current Location</a>
        </div>
        <span class="measurer" aria-hidden="true"></span>
        <input class="location" type="text" placeholder="${placeholder}" />
        <div class="results"></div>
      </div>
    `;

        this.shadowRoot.append(style, container);
        addSharedStylesheet(this.shadowRoot);

        this.$current_location = this.shadowRoot.querySelector(".current_location a");
        this.$location_input = this.shadowRoot.querySelector("input.location");
        this.$measurer = this.shadowRoot.querySelector(".measurer");
        this.$results = this.shadowRoot.querySelector(".results");

        this.min_input_width_px = isMobile() ? 150 : 240;
        this.max_input_width_px = isMobile() ? 185 : 400;
        this.default_placeholder = this.$location_input.placeholder;
        this.location_name = "";
    }

    // Show `name` as the input's text. While the input is focused the name
    // is moved to the placeholder instead so the user can start typing
    // into an empty field (see the focus/blur handlers).
    set_location_name(name) {
        this.location_name = name || "";
        const is_focused = this.shadowRoot.activeElement === this.$location_input;
        if(is_focused) {
            this.$location_input.placeholder = this.location_name || this.default_placeholder;
        } else {
            this.$location_input.value = this.location_name;
            this.$location_input.placeholder = this.default_placeholder;
        }
        this.update_input_size();
    }

    // Resize the input to fit whichever is wider: its current value or
    // its placeholder, constrained by min_input_width_px / max_input_width_px.
    update_input_size() {
        const text = this.$location_input.value || this.$location_input.placeholder || "";
        this.$measurer.textContent = text;
        const width = Math.min(this.max_input_width_px, Math.max(this.$measurer.offsetWidth, this.min_input_width_px));
        this.$location_input.style.width = width + "px";
    }

    connectedCallback() {
        const placeholder_attr = this.getAttribute("placeholder");
        if(placeholder_attr) {
            this.default_placeholder = placeholder_attr;
            this.$location_input.placeholder = placeholder_attr;
        }

        const font_size = this.getAttribute("font_size");
        if(font_size) {
            this.shadowRoot.querySelector(".container input.location").style["font-size"] = font_size;
            this.shadowRoot.querySelector(".container .measurer").style["font-size"] = font_size;
        }

        if(!navigator.geolocation) {
            this.$current_location.parentElement.style.display = "none";
        } else {
            this.$current_location.onclick = this.load_current_location;
        }

        this.$location_input.addEventListener("keydown", function(event) {
            if (event.key === "Enter") {
                this.search(this.$location_input.value);
            }
        }.bind(this));

        this.$location_input.addEventListener("input", () => this.update_input_size());

        this.$location_input.addEventListener("focus", () => {
            if(this.$location_input.value === this.location_name) {
                this.$location_input.value = "";
            }
            this.$location_input.placeholder = this.location_name || this.default_placeholder;
            this.update_input_size();
        });

        this.$location_input.addEventListener("blur", () => {
            if(this.$location_input.value === "") {
                this.$location_input.value = this.location_name;
            }
            this.$location_input.placeholder = this.default_placeholder;
            this.update_input_size();
        });

        this.update_input_size();

        document.addEventListener("click", function(event) {
            const click_path = event.composedPath();
            if(!click_path.includes(this.$results)) {
                this.hideResults();
            }
        }.bind(this));
    }

    load_current_location() {
        navigator.geolocation.getCurrentPosition(
            function(position) {
                this.load_location(position.coords.latitude, position.coords.longitude);
            },
            function(err) {
                console.error("CURRENT LOCATION ERROR", err);
            },
            {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0,
            }
        );
    }

    load_location(lat, lon) {
        this.dispatchEvent(new CustomEvent("latlon_chosen", {
            bubbles: true,
            composed: true,
            detail: {
                lat: lat,
                lon: lon,
            }
        }));
        this.hideResults();
    }

    constructAddress(props, is_address) {
        let parts = [];

        if(is_address) {
            if(props.name && props.osm_key == "place") {
                parts.push(props.name);
            } else if(props.locality) {
                parts.push(props.locality);
            } else {
                if(props.housenumber && props.street) {
                    parts.push(`${props.housenumber} ${props.street}`);
                } else if (props.street) {
                    parts.push(props.street);
                }
            }
        }

        if(props.city) {
            parts.push(props.city);
        } else if(!is_address && props.name) {
            parts.push(props.name);
        }

        if(props.state) {
            parts.push(props.state);
        }

        return parts.join(", ");
    }

    displaySearchResults(results, is_address) {
        this.$results.innerHTML = "";

        const $list = document.createElement("ul");
        $list.classList.add("list");

        const used_addresses = {};

        for(const result of results) {
            const [lon, lat] = result.geometry.coordinates;
            const name = this.constructAddress(result.properties, is_address);
            if(used_addresses[name]) {
                continue;
            }
            used_addresses[name] = true;

            const $a = document.createElement("a");
            $a.href="javascript:;";
            $a.onclick = () => {this.load_location(lat, lon);};
            $a.innerHTML = name;

            const $div = document.createElement("li");
            $div.appendChild($a);
            $list.appendChild($div);
        }

        this.$results.appendChild($list);
    }

    async search(str) {
        str = str.trim();
        if(!str) return;

        this.$results.innerHTML = "";
        this.showResults(true);

        if(str !== this.$location_input.value) {
            this.$location_input.value = str;
        }

        const is_address = this.isAddressLevelSearch(str);

        try {
            let features = [];

            if(is_address) {
                // 1. Try exact address-level results first.
                features = await this.geocode(str, {
                    layers: ["house"],
                    limit: 10,
                });

                // 2. If no exact address, try street-level without house number.
                if(!features.length) {
                    const streetQuery = str.replace(/^\s*\d{1,6}[a-z]?(?:-\d{1,6}[a-z]?)?\s+/, "").trim();

                    if(streetQuery !== str) {
                        features = await this.geocode(streetQuery, {
                            layers: ["street"],
                            limit: 10,
                        });
                    }
                }

                // 3. Broader fallback, but rank address/street results first.
                if(!features.length) {
                    features = await this.geocode(str, {
                        layers: [
                            "house",
                            "street",
                            "locality",
                            "district",
                            "city",
                            "county",
                            "state",
                            "country",
                        ],
                        limit: 20,
                    });

                    features = this.rankGeocodeFeatures(features, true);
                }

                features = features.slice(0, 10);
            } else {
                // For city/town searches, query broadly
                features = await this.geocode(str, {
                    layers: null,
                    limit: 20,
                });

                features = this.filterGeocodeFeatures(features, false).slice(0, 10);
            }

            this.displaySearchResults(features, is_address);
        } catch(error) {
            console.error("Error in search:", error);
        }
    }

    isAddressLevelSearch(query) {
        if(!query || typeof query !== "string") return false;

        const normalized = query
            .toLowerCase()
            .replace(/[.,;:!?'"()]/g, " ")
            .replace(/\s+/g, " ")
            .trim();

        if(!normalized) return false;

        const tokens = normalized.split(" ");
        if(tokens.length < 2) return false;

        const US_STATE_ABBRS = new Set([
            "al","ak","az","ar","ca","co","ct","de","fl","ga","hi","ia","id","il","in",
            "ks","ky","la","ma","md","me","mi","mn","mo","ms","mt","nc","nd","ne","nh",
            "nj","nm","nv","ny","oh","ok","or","pa","ri","sc","sd","tn","tx","ut","va",
            "vt","wa","wi","wv","wy","dc"
        ]);

        const streetTypes = new Set([
            "street", "st", "str",
            "avenue", "ave", "av",
            "road", "rd",
            "boulevard", "blvd", "boul",
            "drive", "dr",
            "lane", "ln",
            "court", "ct",
            "place", "pl",
            "way",
            "parkway", "pkwy",
            "highway", "hwy",
            "freeway", "fwy",
            "expressway", "expy",
            "terrace", "ter", "terr",
            "circle", "cir",
            "trail", "trl",
            "square", "sq",
            "plaza", "plz",
            "alley", "aly",
            "crescent", "cres",
            "route", "rte",
            "turnpike", "tpke",
            "crossing", "xing",
            "walk", "row", "mews", "close"
        ]);

        const fuzzyStreetTypes = [
            "street", "avenue", "road", "boulevard", "drive", "lane", "court",
            "place", "parkway", "highway", "freeway", "expressway", "terrace",
            "circle", "trail", "square", "plaza", "alley", "crescent", "route",
            "turnpike", "crossing"
        ];

        const houseNumberRe = /^\d{1,6}[a-z]?(-\d+[a-z]?)?$/;
        const zipRe = /^\d{5}(-\d{4})?$/;

        // Signal 1: starts with a house number, but avoid ZIP-only patterns.
        if(houseNumberRe.test(tokens[0]) && !zipRe.test(tokens[0])) {
            return true;
        }

        // Signal 2: unit / suite / apartment / floor indicators.
        if(
            /\b(apt|apartment|suite|ste|unit|rm|room|fl|floor)\.?\s*[\d#][\w-]*/i.test(query) ||
            /#\s*\d+[a-z]?/i.test(query)
        ) {
            return true;
        }

        // Signal 3: PO Box.
        if(/\bp\s*\.?\s*o\s*\.?\s*box\s+\d+/i.test(query)) {
            return true;
        }

        const ambiguousAtStart = new Set(["st", "dr", "ft"]);

        for(let i = 0; i < tokens.length; i++) {
            const token = tokens[i];

            if(i === 0 && ambiguousAtStart.has(token)) continue;

            // Important: final 2-letter US state abbreviations are not street suffixes.
            // Fixes "Tolland, CT", "Austin, TX", "Portland, OR", etc.
            if(i === tokens.length - 1 && US_STATE_ABBRS.has(token)) continue;

            const prev = tokens[i - 1];
            const next = tokens[i + 1];

            const prevLooksLikeStreetName =
                !!prev &&
                !US_STATE_ABBRS.has(prev) &&
                !zipRe.test(prev);

            const nextLooksLikeAddressContext =
                !!next &&
                (
                    next === "apt" ||
                    next === "unit" ||
                    next === "suite" ||
                    next === "ste" ||
                    next === "floor" ||
                    next === "fl" ||
                    /^#?\d/.test(next)
                );

            if(streetTypes.has(token)) {
                // Strong signal: "Main St", "Oak Ct", "Broadway Ave Apt 2"
                if(prevLooksLikeStreetName || nextLooksLikeAddressContext) {
                    return true;
                }

                continue;
            }

            // Fuzzy match only for longer tokens, and never against abbreviations.
            if(token.length >= 4) {
                const maxDist = token.length >= 7 ? 2 : 1;

                for(const type of fuzzyStreetTypes) {
                    if(Math.abs(type.length - token.length) > maxDist) continue;

                    if(this.levenshtein(token, type) <= maxDist) {
                        if(prevLooksLikeStreetName || nextLooksLikeAddressContext) {
                            return true;
                        }
                    }
                }
            }
        }

        return false;
    }

    levenshtein(a, b) {
        if(a === b) return 0;
        if(!a.length) return b.length;
        if(!b.length) return a.length;

        let prev = new Array(a.length + 1);
        let curr = new Array(a.length + 1);

        for(let i = 0; i <= a.length; i++) prev[i] = i;

        for(let j = 1; j <= b.length; j++) {
            curr[0] = j;

            for(let i = 1; i <= a.length; i++) {
                const cost = a[i - 1] === b[j - 1] ? 0 : 1;

                curr[i] = Math.min(
                    curr[i - 1] + 1,
                    prev[i] + 1,
                    prev[i - 1] + cost
                );
            }

            [prev, curr] = [curr, prev];
        }

        return prev[a.length];
    }

    async geocode(str, { layers = null, limit = 10 } = {}) {
        const params = new URLSearchParams({
            q: str,
            limit: String(limit),
        });

        if(layers) {
            for(const layer of layers) {
                params.append("layer", layer);
            }
        }

        const response = await fetch(`/geocode?${params}`);
        const data = await response.json();

        if(!data.features || !Array.isArray(data.features)) {
            console.error("Unexpected response format:", data);
            return [];
        }

        return data.features;
    }

    filterGeocodeFeatures(features, isAddressSearch) {
        if(isAddressSearch) return features;

        const tooSpecific = new Set([
            "house",
            "street",
            "venue",
        ]);

        return features.filter(feature => {
            const layer = feature?.properties?.layer;
            return !tooSpecific.has(layer);
        });
    }

    rankGeocodeFeatures(features, isAddressSearch) {
        if(!isAddressSearch) return features;

        const rank = {
            house: 0,
            street: 1,
            venue: 2,
            locality: 5,
            district: 6,
            city: 7,
            county: 8,
            state: 9,
            country: 10,
        };

        return [...features].sort((a, b) => {
            const layerA = a?.properties?.layer;
            const layerB = b?.properties?.layer;

            return (rank[layerA] ?? 99) - (rank[layerB] ?? 99);
        });
    }

    showResults(show_loading=false) {
        if(show_loading) {
            this.$results.innerHTML = "<div class=\"loading\">Loading...</div>";
        }
        this.$results.style.display = "block";
    }

    hideResults() {
        this.$results.style.display = "none";
    }
}

customElements.define("location-search-elem", LocationSearchElem);
export default LocationSearchElem;
